import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  ArrowLeft,
  MapPin,
  Phone,
  CheckCircle2,
  ShoppingBag,
  LogIn,
  LogOut,
  Clock,
  XCircle,
  IndianRupee,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n || 0);

function formatDuration(mins) {
  if (mins == null) return "—";
  if (mins < 1) return `${Math.max(1, Math.round(mins * 60))}s`;
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}

export default function StoreDetail() {
  const { id } = useParams();
  const [store, setStore] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [reasons, setReasons] = useState([]);
  const [noOrderOpen, setNoOrderOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const [savingReason, setSavingReason] = useState(false);
  const [recentNoOrders, setRecentNoOrders] = useState([]);
  const [distance, setDistance] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectAmount, setCollectAmount] = useState("");
  const [collectMode, setCollectMode] = useState("cash");
  const [collectRef, setCollectRef] = useState("");
  const [collectNote, setCollectNote] = useState("");
  const [collectSaving, setCollectSaving] = useState(false);
  const nav = useNavigate();

  // Watch user's GPS to compute distance to store (only if store has coords)
  useEffect(() => {
    if (!store?.lat || !store?.lng || !navigator.geolocation) return;
    let cancelled = false;
    const haversine = (lat1, lon1, lat2, lon2) => {
      const R = 6371000;
      const toRad = (d) => (d * Math.PI) / 180;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(a));
    };
    const watchId = navigator.geolocation.watchPosition(
      (p) => {
        if (cancelled) return;
        const d = haversine(p.coords.latitude, p.coords.longitude, store.lat, store.lng);
        setDistance(d);
      },
      () => setDistance(null),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
    );
    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [store?.lat, store?.lng]);


  const loadActive = async () => {
    try {
      const { data } = await api.get("/visits/active", { params: { store_id: id } });
      setActive(data || null);
    } catch {
      setActive(null);
    }
  };

  const loadRecentNoOrders = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.get("/no-order-reports", {
        params: { date_from: today, date_to: today },
      });
      setRecentNoOrders(data.filter((r) => r.store_id === id));
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    api.get(`/stores/${id}`).then((r) => setStore(r.data));
    loadActive();
    api.get("/no-order-reports/reasons").then((r) => setReasons(r.data));
    loadRecentNoOrders();
    loadLedger();
     
  }, [id]);

  const loadLedger = async () => {
    try {
      const { data } = await api.get(`/stores/${id}/ledger`);
      setLedger(data);
    } catch {
      /* noop */
    }
  };

  const submitCollection = async () => {
    const amt = Number(collectAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setCollectSaving(true);
    try {
      await api.post("/payments", {
        store_id: id,
        amount: amt,
        mode: collectMode,
        reference_no: collectRef || null,
        note: collectNote || null,
      });
      toast.success(`₹${amt.toLocaleString("en-IN")} recorded — pending admin verification`);
      setCollectOpen(false);
      setCollectAmount("");
      setCollectRef("");
      setCollectNote("");
      loadLedger();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setCollectSaving(false);
    }
  };

  useEffect(() => {
    if (!active?.check_in_at) {
      setElapsed(0);
      return;
    }
    const start = new Date(active.check_in_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [active]);

  const withGeo = (cb) => {
    if (!navigator.geolocation) return cb(null, null);
    navigator.geolocation.getCurrentPosition(
      (p) => cb(p.coords.latitude, p.coords.longitude),
      () => cb(null, null),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const doCheckIn = () => {
    setBusy(true);
    withGeo(async (lat, lng) => {
      try {
        const { data } = await api.post("/visits", { store_id: id, lat, lng });
        setActive(data);
        toast.success("Checked in");
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        setBusy(false);
      }
    });
  };

  const doCheckOut = () => {
    if (!active) return;
    setBusy(true);
    withGeo(async (lat, lng) => {
      try {
        const { data } = await api.patch(`/visits/${active.id}/checkout`, { lat, lng });
        toast.success(`Checked out · ${formatDuration(data.duration_minutes)}`);
        setActive(null);
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        setBusy(false);
      }
    });
  };

  const submitNoOrder = () => {
    if (!reason) return toast.error("Pick a reason");
    setSavingReason(true);
    withGeo(async (lat, lng) => {
      try {
        await api.post("/no-order-reports", {
          store_id: id,
          reason,
          note: reasonNote || null,
          lat,
          lng,
        });
        toast.success("No-order reason recorded");
        setNoOrderOpen(false);
        setReason("");
        setReasonNote("");
        loadRecentNoOrders();
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        setSavingReason(false);
      }
    });
  };

  if (!store) return <div className="text-[color:var(--muted)]">Loading store…</div>;

  const elapsedMin = elapsed / 60;

  return (
    <div className="space-y-5" data-testid="store-detail-page">
      <button
        onClick={() => nav(-1)}
        className="flex items-center gap-2 text-sm text-[color:var(--muted)]"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <p className="overline text-[color:var(--muted)]">
          <span
            className={`inline-block text-[10px] px-2 py-0.5 rounded-full mr-2 ${
              store.store_type === "wholesale"
                ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
            }`}
          >
            {store.store_type}
          </span>
        </p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-2">{store.name}</h1>
        {store.owner_name && (
          <p className="text-[color:var(--muted)] text-sm mt-0.5">{store.owner_name}</p>
        )}
      </div>

      <div className="rounded-xl border border-[color:var(--border)] bg-white p-4 space-y-3">
        <div className="flex items-start gap-3">
          <MapPin className="h-4 w-4 mt-1 text-[color:var(--muted)]" />
          <div className="text-sm">{store.address}</div>
        </div>
        {store.phone && (
          <a
            href={`tel:${store.phone}`}
            className="flex items-center gap-3 text-sm text-[color:var(--primary)]"
          >
            <Phone className="h-4 w-4" /> {store.phone}
          </a>
        )}
      </div>

      {ledger && (ledger.outstanding > 0 || ledger.order_count > 0 || ledger.payment_count > 0) && (
        <div
          className={`rounded-xl border p-4 ${
            ledger.outstanding > 0
              ? "border-[color:var(--mustard)]/40 bg-[color:var(--mustard)]/5"
              : "border-[color:var(--moss)]/30 bg-[color:var(--moss)]/5"
          }`}
          data-testid="store-ledger-card"
        >
          <div className="flex items-start gap-3">
            <div
              className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                ledger.outstanding > 0
                  ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
                  : "bg-[color:var(--moss)]/15 text-[color:var(--moss)]"
              }`}
            >
              <IndianRupee className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline text-[color:var(--muted)]">Outstanding</div>
              <div className="font-display text-2xl font-bold tabular-nums" data-testid="store-outstanding">
                {fmtINR(ledger.outstanding)}
              </div>
              <div className="text-xs text-[color:var(--muted)] mt-0.5">
                Billed {fmtINR(ledger.total_billed)} · Paid {fmtINR(ledger.total_paid)}
              </div>
            </div>
          </div>
          {(() => {
            const pending = (ledger.payments || []).filter((p) => !p.verified);
            const pendingTotal = pending.reduce((a, b) => a + (b.amount || 0), 0);
            if (pending.length === 0) return null;
            return (
              <div
                className="mt-3 rounded-lg bg-white/70 border border-[color:var(--mustard)]/40 p-3"
                data-testid="pending-collection-list"
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold uppercase tracking-wide text-[#9A742E]">
                    Awaiting admin verification
                  </span>
                  <span className="tabular-nums font-bold text-[#9A742E]">
                    {fmtINR(pendingTotal)}
                  </span>
                </div>
                <ul className="mt-2 space-y-1 text-xs text-[color:var(--muted)]">
                  {pending.slice(0, 4).map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2">
                      <span className="truncate">
                        {new Date(p.collected_at).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {" · "}
                        {p.mode}
                        {p.reference_no ? ` · ${p.reference_no}` : ""}
                      </span>
                      <span className="tabular-nums shrink-0">{fmtINR(p.amount)}</span>
                    </li>
                  ))}
                </ul>
                {pending.length > 4 && (
                  <div className="text-[10px] text-[color:var(--muted)] mt-1">
                    + {pending.length - 4} more
                  </div>
                )}
              </div>
            );
          })()}
          {ledger.outstanding > 0 && (
            <div className="mt-3 grid grid-cols-4 gap-1 text-[10px]">
              {Object.entries(ledger.ageing).map(([bucket, amt]) => (
                <div
                  key={bucket}
                  className={`rounded p-1.5 text-center ${
                    amt > 0 && (bucket === "60-90" || bucket === "90+")
                      ? "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
                      : amt > 0
                      ? "bg-[color:var(--mustard)]/15 text-[#9A742E]"
                      : "bg-[color:var(--surface-2)] text-[color:var(--muted)]"
                  }`}
                >
                  <div className="uppercase tracking-wide font-semibold">{bucket}d</div>
                  <div className="tabular-nums">{fmtINR(amt)}</div>
                </div>
              ))}
            </div>
          )}
          <Button
            onClick={() => {
              setCollectAmount(ledger.outstanding > 0 ? String(ledger.outstanding) : "");
              setCollectMode("cash");
              setCollectRef("");
              setCollectNote("");
              setCollectOpen(true);
            }}
            className="w-full h-11 rounded-lg bg-[color:var(--ink)] hover:bg-black text-white mt-3"
            data-testid="record-collection-button"
          >
            <Wallet className="h-4 w-4 mr-2" /> Record collection
          </Button>
        </div>
      )}

      {active ? (
        <div
          className="rounded-xl border border-[color:var(--moss)]/30 bg-[color:var(--moss)]/5 p-4"
          data-testid="active-visit-card"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[color:var(--moss)]/15 text-[color:var(--moss)] flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline">You're checked in</div>
              <div className="font-display text-xl font-bold flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {formatDuration(elapsedMin)}
              </div>
              <div className="text-xs text-[color:var(--muted)] mt-0.5">
                Since {new Date(active.check_in_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          </div>
          <Button
            onClick={doCheckOut}
            disabled={busy}
            className="w-full h-14 rounded-xl bg-[color:var(--ink)] hover:bg-black text-white btn-tap text-base mt-4"
            data-testid="check-out-button"
          >
            <LogOut className="h-5 w-5 mr-2" />
            {busy ? "Checking out…" : "Check out"}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {store.lat && store.lng && distance != null && (
            <div
              className={`rounded-lg px-3 py-2 text-xs font-semibold flex items-center gap-2 ${
                distance <= 150
                  ? "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
                  : "bg-[color:var(--primary)]/10 text-[color:var(--primary)]"
              }`}
              data-testid="checkin-distance-hint"
            >
              <MapPin className="h-3.5 w-3.5" />
              {distance <= 150
                ? `You're ${Math.round(distance)}m from the shop — ready to check in`
                : `You're ${Math.round(distance)}m away. Move within 150m to check in.`}
            </div>
          )}
          {store.lat == null || store.lng == null ? (
            <div className="rounded-lg px-3 py-2 text-xs text-[color:var(--muted)] bg-[color:var(--surface-2)]">
              No GPS pin saved for this shop — distance check skipped. Tap edit to drop a pin.
            </div>
          ) : null}
          <Button
            onClick={doCheckIn}
            disabled={busy || (store.lat != null && store.lng != null && distance != null && distance > 150)}
            className="w-full h-14 rounded-xl bg-[color:var(--moss)] hover:bg-[#3c4e41] text-white btn-tap text-base disabled:bg-[color:var(--muted)]/40 disabled:cursor-not-allowed"
            data-testid="check-in-button"
          >
            <LogIn className="h-5 w-5 mr-2" />
            {busy ? "Getting location…" : "Check-in at store"}
          </Button>
        </div>
      )}

      <Link
        to={`/app/stores/${id}/order`}
        className="w-full h-14 rounded-xl bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white btn-tap flex items-center justify-center text-base font-medium gap-2"
        data-testid="new-order-button"
      >
        <ShoppingBag className="h-5 w-5" /> New order
      </Link>

      <Button
        variant="outline"
        onClick={() => setNoOrderOpen(true)}
        className="w-full h-12 rounded-xl border-[color:var(--border)] text-[color:var(--ink)]"
        data-testid="no-order-button"
      >
        <XCircle className="h-4 w-4 mr-2 text-[color:var(--muted)]" /> No order — record reason
      </Button>

      {recentNoOrders.length > 0 && (
        <div
          className="rounded-xl border border-[color:var(--border)] bg-white p-4 space-y-2"
          data-testid="store-no-order-list"
        >
          <div className="overline text-[color:var(--muted)]">Today's no-order log</div>
          {recentNoOrders.map((r) => (
            <div
              key={r.id}
              className="text-sm flex items-start gap-2"
              data-testid={`no-order-entry-${r.id}`}
            >
              <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[color:var(--mustard)]/20 text-[#9A742E] text-[10px] font-bold">
                ✕
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{r.reason_label}</div>
                {r.note && (
                  <div className="text-xs text-[color:var(--muted)] truncate">{r.note}</div>
                )}
                <div className="text-[10px] text-[color:var(--muted)]">
                  {new Date(r.created_at).toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={noOrderOpen} onOpenChange={setNoOrderOpen}>
        <DialogContent className="max-w-md" data-testid="no-order-dialog">
          <DialogHeader>
            <DialogTitle>Why no order at {store.name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-12" data-testid="no-order-reason-trigger">
                  <SelectValue placeholder="Pick a reason" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem
                      key={r.key}
                      value={r.key}
                      data-testid={`no-order-reason-option-${r.key}`}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                placeholder="Any extra context (max 200 chars)"
                maxLength={200}
                rows={3}
                data-testid="no-order-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={submitNoOrder}
              disabled={!reason || savingReason}
              className="w-full h-12 rounded-xl bg-[color:var(--ink)] hover:bg-black text-white"
              data-testid="no-order-submit"
            >
              {savingReason ? "Saving…" : "Record reason"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={collectOpen} onOpenChange={setCollectOpen}>
        <DialogContent className="max-w-md" data-testid="collect-dialog">
          <DialogHeader>
            <DialogTitle>Record collection · {store.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {ledger && ledger.outstanding > 0 && (
              <div className="text-sm text-[color:var(--muted)]">
                Outstanding: <b className="text-[color:var(--ink)]">{fmtINR(ledger.outstanding)}</b>
              </div>
            )}
            <div>
              <Label>Amount (INR)</Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={collectAmount}
                onChange={(e) => setCollectAmount(e.target.value)}
                className="h-12 font-mono text-lg"
                data-testid="collect-amount-input"
                autoFocus
              />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={collectMode} onValueChange={setCollectMode}>
                <SelectTrigger className="h-12" data-testid="collect-mode-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="bank">Bank transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(collectMode === "upi" || collectMode === "cheque" || collectMode === "bank") && (
              <div>
                <Label>Reference / cheque no.</Label>
                <Input
                  value={collectRef}
                  onChange={(e) => setCollectRef(e.target.value)}
                  className="h-12"
                  data-testid="collect-ref-input"
                />
              </div>
            )}
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                value={collectNote}
                onChange={(e) => setCollectNote(e.target.value)}
                rows={2}
                data-testid="collect-note-input"
              />
            </div>
            <div className="text-xs text-[color:var(--muted)] bg-[color:var(--surface-2)] rounded p-2">
              ℹ️ Submitted entries are pending until verified by admin.
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={submitCollection}
              disabled={collectSaving || !collectAmount || Number(collectAmount) <= 0}
              className="w-full h-12 rounded-xl bg-[color:var(--moss)] hover:bg-[#3c4e41] text-white"
              data-testid="collect-submit"
            >
              {collectSaving ? "Saving…" : "Submit collection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
